import { useState, useEffect, useCallback, useMemo } from "react";
import { ShoppingCart, Clock, CheckCircle, FileText, AlertTriangle, Eye } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { AdminSelect } from "./AdminSelect";

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "new", label: "New" },
  { value: "reviewed", label: "Reviewed" },
  { value: "quoted", label: "Quoted" },
  { value: "accepted", label: "Accepted" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_COLORS = {
  new: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  reviewed: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20",
  quoted: "bg-purple-500/15 text-purple-400 border-purple-500/20",
  accepted: "bg-green-500/15 text-green-400 border-green-500/20",
  completed: "bg-green-500/20 text-green-300 border-green-500/25",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/20",
};

function OrderRow({ order, isExpanded, onToggle, onStatusUpdate }) {
  const status = order.order_status || "new";
  const colorCls = STATUS_COLORS[status] || STATUS_COLORS.new;
  const availableStatuses = useMemo(
    () => STATUS_OPTIONS.filter((s) => s.value && s.value !== status),
    [status]
  );

  return (
    <div key={order.id} className="border border-white/[0.06] bg-white/[0.02] rounded overflow-hidden" data-testid={`order-row-${order.id}`}>
      <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-white/[0.02]" onClick={onToggle}>
        <FileText size={14} className="text-white/20 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-white truncate">{order.name || "Anonymous"}</span>
            <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${colorCls}`}>{status}</span>
            {order.project_type && <span className="text-[9px] text-white/25 uppercase tracking-wider">{order.project_type}</span>}
          </div>
          <p className="text-[10px] text-white/30 truncate">{order.email || ""} {order.phone ? `\u00b7 ${order.phone}` : ""}</p>
        </div>
        <span className="text-[10px] text-white/20 shrink-0">{order.submitted_at ? new Date(order.submitted_at).toLocaleDateString() : "\u2014"}</span>
        <Eye size={12} className="text-white/15 shrink-0" />
      </div>
      {isExpanded && (
        <div className="border-t border-white/[0.04] p-4 bg-white/[0.01]">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div><span className="text-[10px] text-white/40 block">Name</span><span className="text-xs text-white">{order.name || "\u2014"}</span></div>
            <div><span className="text-[10px] text-white/40 block">Email</span><span className="text-xs text-white">{order.email || "\u2014"}</span></div>
            <div><span className="text-[10px] text-white/40 block">Phone</span><span className="text-xs text-white">{order.phone || "\u2014"}</span></div>
            <div><span className="text-[10px] text-white/40 block">Type</span><span className="text-xs text-white">{order.project_type || "\u2014"}</span></div>
          </div>
          {order.description && <div className="mb-3"><span className="text-[10px] text-white/40 block mb-1">Description</span><p className="text-xs text-white/70 whitespace-pre-wrap">{order.description}</p></div>}
          {order.dimensions && <div className="mb-3"><span className="text-[10px] text-white/40 block">Dimensions</span><span className="text-xs text-white">{order.dimensions}</span></div>}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/[0.04]">
            <span className="text-[10px] text-white/40">Update status:</span>
            {availableStatuses.map((s) => (
              <button key={s.value} onClick={() => onStatusUpdate(order.id, s.value)} className="text-[10px] px-2 py-1 rounded border border-white/10 text-white/40 hover:text-white hover:border-white/25 transition-colors">
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filter) params.set("status", filter);
      const res = await adminApi.get(`/api/admin/orders?${params}`);
      setOrders(res.data.orders || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error("[AdminOrders] fetch failed:", err); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await adminApi.put(`/api/admin/orders/${orderId}/status`, { status: newStatus });
      fetchOrders();
    } catch (err) { console.error("[AdminOrders] update status failed:", err); }
  };

  return (
    <div className="border border-white/[0.08] bg-[#060606]" data-testid="admin-orders">
      <div className="p-5 border-b border-white/[0.06] flex items-center gap-3 flex-wrap">
        <ShoppingCart size={16} className="text-[#CBA052]" />
        <h3 className="text-sm font-bold text-white">Quote Orders</h3>
        <span className="text-[10px] text-white/30">{total} total</span>
        <AdminSelect value={filter} onChange={(e) => setFilter(e.target.value)} className="ml-auto w-auto" data-testid="orders-filter">
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </AdminSelect>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="text-xs text-white/30 text-center py-8">Loading orders...</div>
        ) : orders.length === 0 ? (
          <div className="text-xs text-white/30 text-center py-8">No orders found.</div>
        ) : (
          <div className="space-y-2" data-testid="orders-list">
            {orders.map((order) => (
              <OrderRow
                key={order.id}
                order={order}
                isExpanded={expanded === order.id}
                onToggle={() => setExpanded(expanded === order.id ? null : order.id)}
                onStatusUpdate={updateStatus}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
